import type { ElementType } from "react";
import {
  Building2,
  CalendarCheck,
  Church,
  ClipboardList,
  DollarSign,
  Heart,
  Mail,
  Users,
  Workflow,
} from "lucide-react";

export type ReportCategoryMetadata = {
  label: string;
  icon: ElementType;
  order: number;
  color: string;
};

const METADATA_FALLBACK: ReportCategoryMetadata = {
  label: "Other",
  icon: ClipboardList,
  order: 999,
  color: "text-muted-foreground",
};

const REGISTERED_REPORT_CATEGORIES: Record<string, ReportCategoryMetadata> = {
  finance: { label: "Finance", icon: DollarSign, order: 1, color: "text-warning" },
  people: { label: "People", icon: Users, order: 2, color: "text-info" },
  attendance: { label: "Attendance", icon: CalendarCheck, order: 3, color: "text-primary" },
  ministry: { label: "Ministry", icon: Church, order: 4, color: "text-accent-purple-600" },
  care: { label: "Care & Ministry", icon: Heart, order: 5, color: "text-primary" },
  services: { label: "Services", icon: Workflow, order: 6, color: "text-primary" },
  ops: { label: "Operations", icon: ClipboardList, order: 7, color: "text-attention" },
  facilities: { label: "Facilities", icon: Building2, order: 8, color: "text-success" },
  messaging: { label: "Messaging", icon: Mail, order: 9, color: "text-accent-purple-600" },
};

export const normalizeReportCategory = (category: string): string => category.trim().toLowerCase();

export const getReportCategoryMetadata = (category: string): ReportCategoryMetadata => {
  const normalized = normalizeReportCategory(category);
  const metadata = REGISTERED_REPORT_CATEGORIES[normalized];
  if (metadata) return metadata;

  const label = category.trim()
    .toLowerCase()
    .split(/[-_]/g)
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ") || METADATA_FALLBACK.label;

  return { ...METADATA_FALLBACK, label };
};

export const getReportCategoryOptions = (categories: Iterable<string>) =>
  Array.from(
    new Set(Array.from(categories).map((category) => normalizeReportCategory(category))).values(),
  )
    .map((category) => ({ id: category, ...getReportCategoryMetadata(category) }))
    .sort((a, b) => (a.order - b.order) || a.label.localeCompare(b.label));
